/**
 * admins コレクションの閲覧・更新。
 * 仕様書: docs/01_spec/spec_user_management.md
 *
 * pending（status='pending'）の招待スタブも含む。UI で「手続中」バッジ表示用。
 * 書込（dept/level/isOwner 変更 / 削除）は owner のみ可能（Firestore Rules + auth 関数で検証）。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import type {
  Admin,
  AdminStatus,
  BankAccount,
  BillingAddress,
  DeliveryAddress,
  DepartmentId,
  Level,
  Role,
} from '@goenhub/shared';

import { db, functions } from '@/lib/firebase';
import { safeUpdateDoc } from '@/lib/safeWrite';

export interface AdminRecord extends Admin {
  /** Firestore document ID。active なら uid、pending なら `pending_<inviteId>`。 */
  uid: string;
  /** 状態（フォールバック含む）。 */
  resolvedStatus: AdminStatus;
}

function resolveStatus(data: Admin): AdminStatus {
  return (data.status ?? 'active') as AdminStatus;
}

export function useAdmins() {
  return useQuery({
    queryKey: ['admins'],
    queryFn: async (): Promise<AdminRecord[]> => {
      const q = query(collection(db, 'admins'), orderBy('createdAt', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => {
        const data = d.data() as Admin;
        return {
          uid: d.id,
          ...data,
          resolvedStatus: resolveStatus(data),
        };
      });
    },
  });
}

// ─── 管理ユーザーの最小ディレクトリ (owner 以外でも使える名前解決) ───

/**
 * listMemberDirectory Callable の返却エントリ。
 * 表示に必要な最小フィールドのみ (機微情報なし)。
 */
export interface AdminDirectoryEntry {
  uid: string;
  displayName: string;
  fullName: string | null;
  email: string;
  dept: string | null;
  level: string | null;
  status: string;
  isOwner: boolean;
  /** useAdmins() の AdminRecord と互換の状態フィールド。 */
  resolvedStatus: AdminStatus;
}

const listMemberDirectoryFn = httpsCallable<
  Record<string, never>,
  { admins: Array<Omit<AdminDirectoryEntry, 'resolvedStatus'>> }
>(functions, 'listMemberDirectory');

/**
 * 管理ユーザー名簿 (最小フィールド、外部SA含む・SAからも呼べる)。
 * ※掲示板用 listAdminDirectory (内部限定・SA除外) とは別物。
 * firestore.rules 上 admins コレクションは owner しか一覧 read できないため、
 * owner 以外 (社員 editor / 外部SA) では Callable 経由で取得する。
 * SA 候補ドロップダウン・担当者名解決など「名前を出すだけ」の用途はこちらを使う。
 */
export function useAdminDirectory(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admin_directory'],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<AdminDirectoryEntry[]> => {
      const r = await listMemberDirectoryFn({});
      return (r.data.admins ?? []).map((a) => ({
        ...a,
        resolvedStatus: (a.status ?? 'active') as AdminStatus,
      }));
    },
  });
}

export function useUpdateAdminRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { uid: string; role: Role }) => {
      await safeUpdateDoc(doc(db, 'admins', args.uid), { role: args.role });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

/**
 * 部署 / レベル / isOwner を一括更新する。
 * onAdminChange Trigger が Custom Claims を再同期する。
 */
export function useUpdateAdminAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      uid: string;
      dept: DepartmentId;
      level: Level;
      isOwner: boolean;
    }) => {
      // 後方互換: role も同時更新
      const role: Role = args.isOwner ? 'owner' : args.level === 'external' ? 'viewer' : 'editor';
      await safeUpdateDoc(doc(db, 'admins', args.uid), {
        dept: args.dept,
        level: args.level,
        isOwner: args.isOwner,
        role,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

export function useDeleteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (adminId: string) => {
      const fn = httpsCallable(functions, 'deleteAdmin');
      const res = await fn({ adminId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

/**
 * 氏名 / 部署 / レベル / isOwner を一括編集（pending・active 両対応）。
 * pending の場合は invitations 側も同期される。
 */
export function useUpdateAdminInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      adminId: string;
      fullName?: string | null;
      dept?: DepartmentId;
      level?: Level;
      isOwner?: boolean;
    }) => {
      const fn = httpsCallable(functions, 'updateAdminInfo');
      const res = await fn(args);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}

/**
 * 単一 admin を uid でリアルタイム取得 (T7 詳細画面用)。
 */
export function useAdmin(uid: string | null | undefined) {
  return useQuery({
    queryKey: ['admins', 'detail', uid],
    enabled: !!uid,
    queryFn: async (): Promise<AdminRecord | null> => {
      if (!uid) return null;
      const snap = await getDoc(doc(db, 'admins', uid));
      if (!snap.exists()) return null;
      const data = snap.data() as Admin;
      return { uid: snap.id, ...data, resolvedStatus: (data.status ?? 'active') as AdminStatus };
    },
  });
}

/**
 * T7: admin プロフィール (住所・振込口座) 更新。
 * 本人 or owner が呼べる (Callable 側で制御)。
 */
export function useUpdateAdminProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      adminId: string;
      billingAddresses?: BillingAddress[];
      deliveryAddresses?: DeliveryAddress[];
      bankAccounts?: BankAccount[];
      invoiceRegistrationNumber?: string | null;
      /** 掲示板通知設定 (Phase 5)。updateAdminProfile 側のホワイトリストと対応。 */
      boardNotify?: {
        email: boolean;
        googleChatWebhookUrl?: string | null;
        chatworkRoomId?: string | null;
      };
    }) => {
      const fn = httpsCallable<typeof args, { ok: true }>(
        functions,
        'updateAdminProfile',
      );
      const res = await fn(args);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admins'] });
      qc.invalidateQueries({ queryKey: ['admins', 'detail', vars.adminId] });
    },
  });
}

/** 販売記録 (T6 で書き込まれる、本 phase は read のみ)。 */
export function useAdminSalesRecords(uid: string | null | undefined) {
  return useQuery({
    queryKey: ['admins', uid, 'sales_records'],
    enabled: !!uid,
    queryFn: async () => {
      if (!uid) return [];
      const snap = await getDocs(
        query(
          collection(db, 'admins', uid, 'sales_records'),
          orderBy('confirmedAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
  });
}

/** 招待を再送する（owner 限定）。 */
export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const fn = httpsCallable(functions, 'resendInvitation');
      const res = await fn({ inviteId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
}
